import { useAssemblyBranchNodeListStore } from '@/stores';
import type {
  AssemblyBranchNodeListService,
  AssemblyChildNodeCommit,
} from '@/services/assembly-branch-node-list';

/**
 * 装配体分支子节点列表加载上下文。
 */
export interface AssemblyBranchNodeListLoadContext {
  /** 当前装配体节点 ID。 */
  readonly assemblyNodeId: string;
  /** 当前装配体分支 ID。 */
  readonly branchId: string;
  /** 当前装配体提交 ID；为空时服务端使用分支 HEAD。 */
  readonly commitId?: string;
  /** 当前装配体节点显示名称。 */
  readonly assemblyNodeName: string;
}

/**
 * 读取子节点提交记录的控制器输入。
 */
export interface AssemblyBranchNodeListLoadNodeCommitsInput {
  /** 子节点 ID。 */
  readonly nodeId: string;
}

/**
 * 切换子节点采纳版本的控制器输入。
 */
export interface AssemblyBranchNodeListSwitchVersionInput {
  /** 子节点 ID。 */
  readonly nodeId: string;
  /** 目标提交 ID。 */
  readonly commitId: string;
}

/**
 * 装配体分支子节点列表控制器依赖。
 */
export interface AssemblyBranchNodeListControllerInput {
  /** 子节点列表 Store，只负责 UI 状态承载。 */
  readonly store: ReturnType<typeof useAssemblyBranchNodeListStore>;
  /** 子节点列表服务，由控制器统一编排调用。 */
  readonly service: AssemblyBranchNodeListService;
  /** 展示错误提示的函数，由 UI 层注入具体消息组件实现。 */
  readonly showError: (text: string) => void;
}

/**
 * 装配体分支子节点列表控制器。
 */
export interface AssemblyBranchNodeListController {
  /**
   * 加载当前装配体分支下的直接子节点列表。
   *
   * @param context 当前装配体节点与分支上下文。
   * @returns 加载成功时返回 true；服务失败时返回 false。
   */
  load(context: AssemblyBranchNodeListLoadContext): Promise<boolean>;

  /**
   * 加载某个子节点的提交记录。
   *
   * @param input 子节点提交记录查询输入。
   * @returns 子节点提交记录列表；上下文缺失或服务失败时返回空数组。
   */
  loadNodeCommits(input: AssemblyBranchNodeListLoadNodeCommitsInput): Promise<readonly AssemblyChildNodeCommit[]>;

  /**
   * 切换父装配体对子节点采纳的提交版本。
   *
   * @param input 采纳版本切换输入。
   */
  switchAdoptedNodeVersion(input: AssemblyBranchNodeListSwitchVersionInput): Promise<void>;

  /**
   * 装配结构变化后刷新列表。
   */
  refreshAfterAssemblyStructureChange(): Promise<void>;

  /**
   * 重置列表面板状态。
   */
  reset(): void;
}

/**
 * 控制器内部使用的完整服务上下文。
 */
interface AssemblyBranchNodeListServiceContext {
  /** 当前装配体节点 ID。 */
  readonly assemblyNodeId: string;
  /** 当前装配体分支 ID。 */
  readonly branchId: string;
  /** 当前装配体提交 ID；为空时服务端使用分支 HEAD。 */
  readonly assemblyCommitId?: string;
}

/**
 * 把未知异常转换为可展示文本。
 *
 * @param error 捕获到的未知异常。
 * @returns 可用于 Store 和消息提示的错误文本。
 */
function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === 'string' && error.trim().length > 0) {
    return error;
  }

  return '未知错误';
}

/**
 * 从 Store 中读取完整服务上下文。
 *
 * @param store 子节点列表 Store。
 * @returns 若装配体节点 ID 和分支 ID 都存在，则返回服务上下文；否则返回 null。
 */
function resolveServiceContext(
  store: ReturnType<typeof useAssemblyBranchNodeListStore>,
): AssemblyBranchNodeListServiceContext | null {
  if (!store.currentAssemblyNodeId || !store.currentBranchId) {
    return null;
  }

  return {
    assemblyNodeId: store.currentAssemblyNodeId,
    branchId: store.currentBranchId,
    ...(store.currentCommitId ? { assemblyCommitId: store.currentCommitId } : {}),
  };
}

/**
 * 从 Store 中读取完整加载上下文。
 *
 * @param store 子节点列表 Store。
 * @returns 若面板上下文字段完整，则返回可复用的加载上下文；否则返回 null。
 */
function resolveLoadContext(
  store: ReturnType<typeof useAssemblyBranchNodeListStore>,
): AssemblyBranchNodeListLoadContext | null {
  if (!store.currentAssemblyNodeId || !store.currentBranchId || !store.currentAssemblyNodeName) {
    return null;
  }

  return {
    assemblyNodeId: store.currentAssemblyNodeId,
    branchId: store.currentBranchId,
    ...(store.currentCommitId ? { commitId: store.currentCommitId } : {}),
    assemblyNodeName: store.currentAssemblyNodeName,
  };
}

/**
 * 处理控制器错误并交给 UI 全局提示。
 *
 * 设计说明：分支节点列表只承载真实节点数据；接口失败不能混入列表区域，否则会把“列表项”和
 * “一次性错误反馈”耦合在一起。这里统一只弹出 message，让列表保持空态或已有数据状态。
 *
 * @param input 控制器依赖。
 * @param prefix 错误所属动作的中文前缀。
 * @param error 捕获到的未知异常。
 */
function handleControllerError(input: AssemblyBranchNodeListControllerInput, prefix: string, error: unknown): void {
  const message = `${prefix}：${toErrorMessage(error)}`;

  input.showError(message);
}

/**
 * 在缺少装配体分支上下文时给出统一错误提示。
 *
 * @param input 控制器依赖。
 */
function showMissingContextError(input: AssemblyBranchNodeListControllerInput): void {
  const message = '缺少当前装配体分支上下文，无法执行操作';

  input.showError(message);
}

/**
 * 创建装配体分支子节点列表控制器。
 *
 * 设计说明：控制器只编排服务和 Store，确认弹窗、错误提示以及装配体重载都由调用方注入或直接处理。
 * 当前“创建子节点”和“新增负责分支”入口已经下线，因此这里不再暴露对应写操作，避免 UI 以外的调用方继续触发旧链路。
 *
 * @param input 控制器依赖。
 * @returns 装配体分支子节点列表控制器。
 */
export function createAssemblyBranchNodeListController(
  input: AssemblyBranchNodeListControllerInput,
): AssemblyBranchNodeListController {
  /**
   * 加载列表并写入 Store。
   *
   * @param context 当前装配体节点与分支上下文。
   * @returns 加载成功时返回 true；服务失败时返回 false。
   */
  async function load(context: AssemblyBranchNodeListLoadContext): Promise<boolean> {
    input.store.setContext(context);
    // 切换装配体或分支上下文后，旧节点已经不再属于当前面板；先清空可以避免失败时出现“新上下文 + 旧节点”。
    input.store.setNodes([]);
    input.store.isLoading = true;

    try {
      // 服务层只需要节点和分支 ID，显示名称留在 Store 中供 UI 使用。
      const nodes = await input.service.listChildNodes({
        assemblyNodeId: context.assemblyNodeId,
        branchId: context.branchId,
        ...(context.commitId ? { assemblyCommitId: context.commitId } : {}),
      });
      input.store.setNodes(nodes);
      return true;
    } catch (error) {
      handleControllerError(input, '加载装配体子节点失败', error);
      return false;
    } finally {
      input.store.isLoading = false;
    }
  }

  /**
   * 加载子节点提交记录。
   *
   * @param commitsInput 提交记录查询输入。
   * @returns 子节点提交记录列表；上下文缺失或服务失败时返回空数组。
   */
  async function loadNodeCommits(
    commitsInput: AssemblyBranchNodeListLoadNodeCommitsInput,
  ): Promise<readonly AssemblyChildNodeCommit[]> {
    const context = resolveServiceContext(input.store);

    if (!context) {
      showMissingContextError(input);
      return [];
    }

    try {
      return await input.service.listNodeCommits({
        ...context,
        nodeId: commitsInput.nodeId,
      });
    } catch (error) {
      handleControllerError(input, '加载子节点提交记录失败', error);
      return [];
    }
  }

  /**
   * 切换采纳版本。
   *
   * @param versionInput 采纳版本切换输入。
   */
  async function switchAdoptedNodeVersion(versionInput: AssemblyBranchNodeListSwitchVersionInput): Promise<void> {
    void versionInput;
    input.showError('旧子节点采纳版本写接口已停用，请使用 Editor 的项目模型局部切换入口。');
  }

  /**
   * 装配结构变化后刷新列表。
   */
  async function refreshAfterAssemblyStructureChange(): Promise<void> {
    const context = resolveLoadContext(input.store);

    if (!context) {
      return;
    }

    await load(context);
  }

  /**
   * 重置控制器对应的 Store 状态。
   */
  function reset(): void {
    input.store.reset();
  }

  return {
    load,
    loadNodeCommits,
    switchAdoptedNodeVersion,
    refreshAfterAssemblyStructureChange,
    reset,
  };
}
